import { useForm } from "@tanstack/react-form";
import { createFileRoute, useRouteContext } from "@tanstack/react-router";
import { useState } from "react";
import { useDropzone } from "react-dropzone";
import {
	graphql,
	useFragment,
	useLazyLoadQuery,
	useMutation,
} from "react-relay";
import { toast } from "sonner";
import z from "zod";
import { Icon } from "@/components/icon";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CardTitle } from "@/components/ui/card";
import {
	Field,
	FieldDescription,
	FieldError,
	FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { useFileUpload } from "@/hooks/useFileUpload";
import { cn } from "@/utils";
import type { personalizacaoIA_aiPersona$key } from "./__generated__/personalizacaoIA_aiPersona.graphql";
import type { personalizacaoIA_niches$key } from "./__generated__/personalizacaoIA_niches.graphql";
import type { personalizacaoIACreateMutation } from "./__generated__/personalizacaoIACreateMutation.graphql";
import type { personalizacaoIAQuery } from "./__generated__/personalizacaoIAQuery.graphql";
import type { personalizacaoIAUpdateMutation } from "./__generated__/personalizacaoIAUpdateMutation.graphql";

export const Route = createFileRoute(
	"/_logged/c/$club/(sidebar)/(admins)/ia/personalizacao",
)({
	component: RouteComponent,
});

function AIConfigurationCard({
	aiPersonaRef,
	nichesRef,
	clubSlug,
}: {
	aiPersonaRef: personalizacaoIA_aiPersona$key | null | undefined;
	nichesRef: personalizacaoIA_niches$key;
	clubSlug: string;
}) {
	const { session } = useRouteContext({ from: "__root__" });

	const aiPersona = useFragment(
		graphql`
			fragment personalizacaoIA_aiPersona on AiPersona {
				id
				rawID
				name
				description
				photo {
					id
					signedUrl
				}
				niches {
					id
					name
				}
			}
		`,
		aiPersonaRef,
	);

	const niches = useFragment(
		graphql`
			fragment personalizacaoIA_niches on Query {
				aiPersonaNiches {
					id
					name
				}
			}
		`,
		nichesRef,
	);

	const [isEditing, setIsEditing] = useState(false);
	const [isPhotoHovering, setIsPhotoHovering] = useState(false);

	const [createAiPersona] = useMutation<personalizacaoIACreateMutation>(graphql`
		mutation personalizacaoIACreateMutation($input: AiPersonaCreateInput!) {
			aiPersonaCreate(input: $input) {
				id
				...personalizacaoIA_aiPersona
			}
		}
	`);

	const [updateAiPersona] = useMutation<personalizacaoIAUpdateMutation>(graphql`
		mutation personalizacaoIAUpdateMutation($input: AiPersonaUpdateInput!) {
			aiPersonaUpdate(input: $input) {
				id
				...personalizacaoIA_aiPersona
			}
		}
	`);

	const { upload: uploadPhoto } = useFileUpload({ club: clubSlug, session });

	const form = useForm({
		defaultValues: {
			name: aiPersona?.name || "",
			description: aiPersona?.description || "",
			...(aiPersona?.photo
				? {
						photo: {
							id: aiPersona.photo.id,
							url: aiPersona.photo.signedUrl,
						},
					}
				: {}),
			niches: aiPersona?.niches.map((n) => n.id) || ([] as string[]),
		},
		validators: {
			onChange: z.object({
				name: z
					.string()
					.min(1, "Nome da IA é obrigatório")
					.max(64, "Nome pode ter no máximo 64 caracteres"),
				description: z
					.string()
					.min(1, "Descrição do comportamento é obrigatória"),
				photo: z
					.object({
						id: z.string(),
						url: z.url(),
					})
					.optional(),
				niches: z.array(z.string()),
			}),
		},
		onSubmit: ({ value }) => {
			const nicheIds = value.niches.map((id) => parseInt(id, 10));

			if (aiPersona) {
				// Update existing
				return new Promise((resolve, reject) => {
					updateAiPersona({
						variables: {
							input: {
								id: aiPersona.id,
								name: value.name,
								description: value.description,
								photo: value.photo?.id || null,
								niches: nicheIds,
							},
						},
						onCompleted: (response, errors) => {
							if (errors?.length) {
								toast.error(errors[0].message);
								reject(new Error(errors[0].message));
								return;
							}
							toast.success("Configurações da IA atualizadas com sucesso!");
							setIsEditing(false);
							resolve(response);
						},
						onError: (error) => {
							toast.error("Erro ao atualizar configurações. Tente novamente.");
							reject(error);
						},
					});
				});
			}

			// Create new
			return new Promise((resolve, reject) => {
				createAiPersona({
					variables: {
						input: {
							club: clubSlug,
							name: value.name,
							description: value.description,
							photo: value.photo?.id,
							niches: nicheIds,
							enabled: true,
						},
					},
					onCompleted: (response, errors) => {
						if (errors?.length) {
							toast.error(errors[0].message);
							reject(new Error(errors[0].message));
							return;
						}
						toast.success("IA configurada com sucesso!");
						setIsEditing(false);
						resolve(response);
					},
					onError: (error) => {
						toast.error("Erro ao configurar IA. Tente novamente.");
						reject(error);
					},
				});
			});
		},
	});

	const {
		getRootProps: getPhotoRootProps,
		getInputProps: getPhotoInputProps,
		open: openPhotoPicker,
	} = useDropzone({
		noClick: true,
		accept: { "image/*": [] },
		disabled: !isEditing,
		onDrop: ([file]) => {
			if (file) {
				uploadPhoto(file).then((res) => {
					form.setFieldValue("photo", { id: res.globalId, url: res.url });
				});
			}
		},
	});

	const toggleNiche = (nicheId: string) => {
		const currentNiches = form.getFieldValue("niches");
		if (currentNiches.includes(nicheId)) {
			form.setFieldValue(
				"niches",
				currentNiches.filter((id) => id !== nicheId),
			);
		} else {
			form.setFieldValue("niches", [...currentNiches, nicheId]);
		}
	};

	return (
		<form
			className="overflow-hidden rounded-md border py-6 px-6 flex flex-col gap-6"
			onSubmit={(e) => {
				e.preventDefault();
				e.stopPropagation();
				form.handleSubmit();
			}}
		>
			<div className="flex flex-row items-center justify-between">
				<CardTitle>Configurações da IA</CardTitle>

				{isEditing ? (
					<form.Subscribe
						selector={(state) => [state.isSubmitting, state.canSubmit]}
					>
						{([isSubmitting, canSubmit]) => (
							<Button type="submit" disabled={isSubmitting || !canSubmit}>
								{isSubmitting && <Spinner />}
								Salvar
							</Button>
						)}
					</form.Subscribe>
				) : (
					<Button
						type="button"
						variant="outline"
						onClick={() => setIsEditing(true)}
					>
						<Icon name="Pencil" />
						Editar
					</Button>
				)}
			</div>

			<div className="w-full pt-3 flex flex-col gap-6">
				<form.Field name="photo">
					{(field) => (
						<div className="flex flex-row gap-3">
							<div className="flex flex-col flex-1">
								<span className="text-sm text-foreground font-medium">
									Foto de Perfil
								</span>

								<span className="text-sm mt-1 text-muted-foreground">
									Dimensões Recomendadas: 256x256px
								</span>
							</div>

							{/* biome-ignore lint/a11y/noStaticElementInteractions: expected */}
							<div
								onMouseEnter={() => setIsPhotoHovering(true)}
								onMouseLeave={() => setIsPhotoHovering(false)}
								className="relative w-fit"
								{...getPhotoRootProps()}
							>
								<input {...getPhotoInputProps()} className="hidden" />
								<Avatar className="size-12 border border-dashed">
									<AvatarImage src={field.state.value?.url} />
									<AvatarFallback>
										<Icon name="Bot" className="size-6 text-muted-foreground" />
									</AvatarFallback>
								</Avatar>
								{isEditing && isPhotoHovering && (
									<button
										type="button"
										onClick={() => openPhotoPicker()}
										className="inset-0 absolute size-12 flex items-center justify-center border border-primary rounded-full bg-primary/10 text-primary border-dashed"
									>
										<Icon name="Upload" className="size-5" />
									</button>
								)}
							</div>
						</div>
					)}
				</form.Field>

				<Separator />

				<form.Field name="name">
					{(field) => (
						<Field className="flex flex-col lg:flex-row lg:gap-6 lg:items-start lg:justify-between">
							<div className="flex flex-col gap-1.5">
								<FieldLabel>Nome da IA</FieldLabel>

								<FieldDescription>
									Nome que identifica sua IA. Aparecerá nas conversas e
									interações com os membros.
								</FieldDescription>
							</div>

							<div className="flex flex-col gap-1.5 w-full max-w-md">
								<Input
									className="w-full"
									id={field.name}
									name={field.name}
									value={field.state.value}
									onBlur={field.handleBlur}
									onChange={(e) => field.handleChange(e.target.value)}
									placeholder="Ex: Assistente Virtual"
									disabled={!isEditing}
									aria-invalid={
										!!(field.state.meta.isTouched && field.state.meta.errors[0])
									}
								/>
								{field.state.meta.isTouched && field.state.meta.errors[0] ? (
									<FieldError errors={field.state.meta.errors} />
								) : null}
							</div>
						</Field>
					)}
				</form.Field>

				<Separator />

				<form.Field name="description">
					{(field) => (
						<Field className="flex flex-col lg:flex-row lg:gap-6 lg:items-start lg:justify-between">
							<div className="flex flex-col gap-1.5">
								<FieldLabel>Descrição do Comportamento</FieldLabel>

								<FieldDescription>
									Descreva como a IA deve se comportar, qual o tom de voz e como
									ela deve interagir com os membros.
								</FieldDescription>
							</div>

							<div className="flex flex-col gap-1.5 w-full max-w-md">
								<Textarea
									className="w-full min-h-[120px]"
									id={field.name}
									name={field.name}
									value={field.state.value}
									onBlur={field.handleBlur}
									onChange={(e) => field.handleChange(e.target.value)}
									placeholder="Descreva o comportamento da sua IA..."
									disabled={!isEditing}
									aria-invalid={
										!!(field.state.meta.isTouched && field.state.meta.errors[0])
									}
								/>
								{field.state.meta.isTouched && field.state.meta.errors[0] ? (
									<FieldError errors={field.state.meta.errors} />
								) : null}
							</div>
						</Field>
					)}
				</form.Field>

				<Separator />

				<form.Field name="niches">
					{(field) => (
						<Field className="flex flex-col lg:flex-row lg:gap-6 lg:items-start lg:justify-between">
							<div className="flex flex-col gap-1.5 flex-1">
								<FieldLabel>Tópicos Relevantes</FieldLabel>

								<FieldDescription>
									Selecione os tópicos que são relevantes para área de atuação
									da sua IA.
								</FieldDescription>
							</div>

							<div className="flex flex-wrap gap-2 w-full max-w-md">
								{niches.aiPersonaNiches.map((niche) => {
									const isSelected = field.state.value.includes(niche.id);
									return (
										<button
											key={niche.id}
											type="button"
											disabled={!isEditing}
											onClick={() => toggleNiche(niche.id)}
											className="focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
										>
											<Badge
												variant={isSelected ? "default" : "outline"}
												className={cn(
													"cursor-pointer transition-colors",
													isSelected && "bg-primary text-primary-foreground",
													!isSelected && "hover:bg-accent",
												)}
											>
												{niche.name}
											</Badge>
										</button>
									);
								})}
							</div>
						</Field>
					)}
				</form.Field>
			</div>
		</form>
	);
}

function RouteComponent() {
	const { club } = useRouteContext({ from: "/_logged/c/$club" });

	const data = useLazyLoadQuery<personalizacaoIAQuery>(
		graphql`
			query personalizacaoIAQuery($slug: String!) {
				club(slug: $slug) {
					id
					aiPersona {
						...personalizacaoIA_aiPersona
					}
				}
				...personalizacaoIA_niches
			}
		`,
		{ slug: club.slug },
	);

	return (
		<div className="p-6 flex flex-col gap-6">
			<AIConfigurationCard
				aiPersonaRef={data.club.aiPersona}
				nichesRef={data}
				clubSlug={club.slug}
			/>
		</div>
	);
}
